import React from "react"
import {
  Filters as SearchFilters,
  FiltersType,
} from "~/react/components/search/filters"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSubmit,
} from "~/react/ui/dropdown-menu"
import { Button } from "~/react/ui/button"
import { Checkbox } from "~/react/ui/checkbox"
import { Icon } from "~/react/ui/icon"

export type { FiltersType as LookalikesFiltersType }

interface FiltersProps {
  filters: FiltersType
  setFilters: (filters: FiltersType) => void
  jobTitles: string[]
  clearFilters: () => void
  anyFiltersSet: boolean
  sourceKeywords?: string[]
  selectedKeywords: string[]
  setSelectedKeywords: (keywords: string[]) => void
}

export const LookalikesFilters: React.FC<FiltersProps> = ({
  filters,
  setFilters,
  jobTitles,
  clearFilters,
  anyFiltersSet,
  sourceKeywords = [],
  selectedKeywords,
  setSelectedKeywords,
}) => {
  const [tempSelectedKeywords, setTempSelectedKeywords] = React.useState<string[]>(selectedKeywords)

  React.useEffect(() => {
    setTempSelectedKeywords(selectedKeywords)
  }, [selectedKeywords])

  const toggleKeyword = (keyword: string) => {
    setTempSelectedKeywords((prev) =>
      prev.includes(keyword)
        ? prev.filter((k) => k !== keyword)
        : [...prev, keyword]
    )
  }

  const applyKeywordFilter = () => {
    setSelectedKeywords(tempSelectedKeywords)
  }

  const removeKeyword = (keyword: string) => {
    setSelectedKeywords(selectedKeywords.filter((k) => k !== keyword))
  }

  const hasKeywordFilters = selectedKeywords.length > 0 && selectedKeywords.length < sourceKeywords.length
  const anyFiltersIncludingKeywords = anyFiltersSet || hasKeywordFilters

  const handleClearFilters = () => {
    clearFilters()
    setSelectedKeywords([])
  }

  // Domain Keywords Filter component
  const domainKeywordsFilter = sourceKeywords.length > 0 && (
    <div className="h-filter">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="h-filter__button" size="sm">
            Domain keywords
            <Icon icon="angle-down" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {sourceKeywords.map((keyword) => (
            <DropdownMenuItem key={keyword}>
              <Checkbox
                checked={tempSelectedKeywords.includes(keyword)}
                id={`filter-keyword-${keyword}`}
                name="filter-keyword"
                onChange={() => toggleKeyword(keyword)}
              />
              <label htmlFor={`filter-keyword-${keyword}`}>{keyword}</label>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSubmit onClick={applyKeywordFilter}>
            Apply filters
          </DropdownMenuSubmit>
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedKeywords.map((keyword) => (
        <div className="h-filter__value" key={keyword}>
          {keyword}
          <button
            className="btn-empty h-filter__remove"
            onClick={() => removeKeyword(keyword)}
          >
            <Icon icon="times" />
          </button>
        </div>
      ))}
    </div>
  )

  return (
    <SearchFilters
      possibleFilters={[
        "department",
        "job_title",
        "verification_status",
        "required_field",
        "location",
        "similarity_score",
        "already_saved",
      ]}
      filters={filters}
      setFilters={setFilters}
      jobTitles={jobTitles}
      clearFilters={handleClearFilters}
      anyFiltersSet={anyFiltersIncludingKeywords}
      additionalFilter={domainKeywordsFilter}
    />
  )
}
